import { Badge } from "@/components/ui/badge";
import { 
  Clock, 
  PlayCircle, 
  Package, 
  Truck, 
  CheckCircle2, 
  XCircle, 
  Archive,
  AlertCircle
} from "lucide-react";
import { cn } from "@/lib/utils";

interface JobStatusBadgeProps {
  status: string;
  collectionCompleted?: boolean;
  collectionSkipped?: boolean;
  deliveryCompleted?: boolean;
  isDraft?: boolean;
  showProgress?: boolean;
  size?: "sm" | "md" | "lg";
  cancelledAt?: string | null;
  cancelReason?: string | null;
}

export const JobStatusBadge = ({
  status,
  collectionCompleted = false,
  collectionSkipped = false,
  deliveryCompleted = false,
  isDraft = false,
  showProgress = false,
  size = "md",
  cancelledAt,
  cancelReason,
}: JobStatusBadgeProps) => {
  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-xs px-2.5 py-1",
    lg: "text-sm px-3 py-1.5"
  };

  const iconSizes = {
    sm: "w-3 h-3",
    md: "w-3.5 h-3.5",
    lg: "w-4 h-4"
  };

  // Determine the actual progress stage
  const getProgressStage = () => {
    // cancelled_at is the source of truth for cancellation, regardless of status field
    if (cancelledAt) return "aborted";
    if (status === "cancelled") return "aborted";
    if (status === "completed" || deliveryCompleted) return "completed";
    if (status === "archived") return "archived";
    if (collectionCompleted || collectionSkipped) return "awaiting_delivery";
    if (status === "in_progress") return "in_progress";
    if (isDraft) return "draft";
    return "pending";
  };

  const progressStage = getProgressStage();

  const stageConfig = {
    draft: {
      label: "Draft",
      icon: Clock,
      className: "bg-muted text-muted-foreground border-muted-foreground/20",
      dotColor: "bg-muted-foreground"
    },
    pending: {
      label: "Pending",
      icon: Clock,
      className: "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-400 dark:border-orange-800",
      dotColor: "bg-orange-500"
    },
    in_progress: {
      label: "In Progress",
      icon: PlayCircle,
      className: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:border-blue-800",
      dotColor: "bg-blue-500"
    },
    awaiting_delivery: {
      label: "Awaiting Delivery",
      icon: Truck,
      className: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800",
      dotColor: "bg-amber-500"
    },
    completed: {
      label: "Completed",
      icon: CheckCircle2,
      className: "bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800",
      dotColor: "bg-green-500"
    },
    aborted: {
      label: cancelReason ? `Aborted — ${cancelReason}` : "Aborted",
      icon: XCircle,
      className: "bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-400 dark:border-red-800",
      dotColor: "bg-red-500"
    },
    cancelled: {
      label: "Cancelled",
      icon: XCircle,
      className: "bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-400 dark:border-red-800",
      dotColor: "bg-red-500"
    },
    archived: {
      label: "Archived",
      icon: Archive,
      className: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-700",
      dotColor: "bg-slate-500"
    }
  };

  const config = stageConfig[progressStage];
  const Icon = config.icon;

  if (showProgress) {
    // Show visual progress indicator
    const stages = ["pending", "in_progress", "awaiting_delivery", "completed"];
    const currentIndex = stages.indexOf(progressStage);
    
    return (
      <div className="flex flex-col gap-2">
        <Badge 
          variant="outline" 
          className={cn(
            "font-medium flex items-center gap-1.5 border",
            config.className,
            sizeClasses[size]
          )}
        >
          <Icon className={iconSizes[size]} />
          {config.label}
        </Badge>
        
        {progressStage !== "aborted" && progressStage !== "archived" && progressStage !== "draft" && (
          <div className="flex items-center gap-1">
            {stages.map((stage, index) => (
              <div
                key={stage}
                className={cn(
                  "h-1.5 flex-1 rounded-full transition-colors",
                  index <= currentIndex 
                    ? config.dotColor
                    : "bg-muted"
                )}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Badge 
      variant="outline" 
      className={cn(
        "font-medium flex items-center gap-1.5 border",
        config.className,
        sizeClasses[size]
      )}
    >
      <Icon className={iconSizes[size]} />
      {config.label}
    </Badge>
  );
};

// POC/POD Status Badge
interface AppraisalStatusBadgeProps {
  type: "POC" | "POD";
  completed?: boolean;
  skipped?: boolean;
  size?: "sm" | "md";
}

export const AppraisalStatusBadge = ({
  type,
  completed = false,
  skipped = false,
  size = "sm"
}: AppraisalStatusBadgeProps) => {
  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-xs px-2.5 py-1"
  };

  if (skipped) {
    return (
      <Badge 
        variant="outline" 
        className={cn(
          "bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-400 dark:border-red-800",
          sizeClasses[size]
        )}
      >
        <AlertCircle className="w-3 h-3 mr-1" />
        {type} Skipped
      </Badge>
    );
  }

  if (completed) {
    return (
      <Badge 
        variant="outline" 
        className={cn(
          "bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800",
          sizeClasses[size]
        )}
      >
        <CheckCircle2 className="w-3 h-3 mr-1" />
        {type} Complete
      </Badge>
    );
  }

  return (
    <Badge 
      variant="outline" 
      className={cn(
        "bg-muted text-muted-foreground border-muted-foreground/20",
        sizeClasses[size]
      )}
    >
      <Clock className="w-3 h-3 mr-1" />
      {type} Pending
    </Badge>
  );
};

// Partial Appraisal Badge - shows when exterior or interior was skipped
interface PartialAppraisalBadgeProps {
  hasPhotosOnly?: boolean;
  skippedExterior?: boolean;
  skippedInterior?: boolean;
  size?: "sm" | "md";
}

export const PartialAppraisalBadge = ({
  hasPhotosOnly = false,
  skippedExterior = false,
  skippedInterior = false,
  size = "sm"
}: PartialAppraisalBadgeProps) => {
  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-xs px-2.5 py-1"
  };

  if (hasPhotosOnly) {
    return (
      <Badge 
        variant="outline" 
        className={cn(
          "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800",
          sizeClasses[size]
        )}
      >
        <AlertCircle className="w-3 h-3 mr-1" />
        Photos Only
      </Badge>
    );
  }

  if (skippedExterior && skippedInterior) {
    return (
      <Badge 
        variant="outline" 
        className={cn(
          "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800",
          sizeClasses[size]
        )}
      >
        <AlertCircle className="w-3 h-3 mr-1" />
        Photos Only
      </Badge>
    );
  }

  if (skippedInterior) {
    return (
      <Badge 
        variant="outline" 
        className={cn(
          "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-400 dark:border-yellow-800",
          sizeClasses[size]
        )}
      >
        <AlertCircle className="w-3 h-3 mr-1" />
        Interior Skipped
      </Badge>
    );
  }

  return null;
};
